import { useState, useMemo, useRef, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { DollarSign, ArrowLeft, TrendingDown, Clock, Wrench, Award, BarChart3, ChevronRight, Search } from 'lucide-react';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { CATEGORIES_MFR } from '../data/manufacturers';

const CATEGORIES = [
  { slug: 'ultraleggeri', name: 'Ultraleggeri', depRate: 0.025, maxHrs: 3000, baseMultiplier: 1 },
  { slug: 'propulsione-singola', name: 'Propulsione Singola', depRate: 0.03, maxHrs: 10000, baseMultiplier: 1 },
  { slug: 'propulsione-multipla', name: 'Propulsione Multipla', depRate: 0.035, maxHrs: 10000, baseMultiplier: 1 },
  { slug: 'turboelica', name: 'Turboelica', depRate: 0.04, maxHrs: 15000, baseMultiplier: 1 },
  { slug: 'jet', name: 'Jet', depRate: 0.045, maxHrs: 18000, baseMultiplier: 1 },
  { slug: 'elicotteri', name: 'Elicotteri', depRate: 0.04, maxHrs: 12000, baseMultiplier: 1 },
];

const CONDITIONS = [
  { value: 'new', label: 'Nuovo di fabbrica', factor: 1.0 },
  { value: 'like-new', label: 'Come nuovo', factor: 0.92 },
  { value: 'excellent', label: 'Eccellente', factor: 0.82 },
  { value: 'good', label: 'Buone condizioni', factor: 0.68 },
  { value: 'fair', label: 'Condizioni discrete', factor: 0.52 },
  { value: 'project', label: 'Da restaurare', factor: 0.28 },
];

const AVIONICS = [
  { value: 'steam', label: 'Strumenti analogici (steam gauges)', factor: 0.88 },
  { value: 'basic-digital', label: 'Digitale base (singolo schermo)', factor: 0.95 },
  { value: 'glass', label: 'Glass cockpit (Garmin G1000 / similar)', factor: 1.05 },
  { value: 'advanced', label: 'Glass avanzato (G3000, Collins, Honeywell)', factor: 1.12 },
  { value: 'full-ifr', label: 'Full IFR + autopilot certificato', factor: 1.08 },
];

// Base price ranges per category (used when no specific model DB)
const BASE_PRICES = {
  'ultraleggeri': { min: 20000, max: 160000 },
  'propulsione-singola': { min: 80000, max: 950000 },
  'propulsione-multipla': { min: 200000, max: 2500000 },
  'turboelica': { min: 500000, max: 10000000 },
  'jet': { min: 1000000, max: 65000000 },
  'elicotteri': { min: 150000, max: 20000000 },
};

function ManufacturerSearch({ value, onChange, manufacturers }) {
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const ref = useRef(null);

  const filtered = useMemo(() => {
    if (!query) return manufacturers.slice(0, 30);
    return manufacturers.filter(m => m.toLowerCase().includes(query.toLowerCase())).slice(0, 30);
  }, [query, manufacturers]);

  useEffect(() => {
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  return (
    <div className="relative" ref={ref}>
      <div className="relative">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-500" />
        <Input
          value={open ? query : value}
          onChange={e => { setQuery(e.target.value); setOpen(true); }}
          onFocus={() => setOpen(true)}
          placeholder="Cerca costruttore..."
          className="bg-zinc-800 border-zinc-700 text-white h-10 text-sm pl-9"
          data-testid="est-manufacturer-search"
        />
      </div>
      {open && (
        <div className="absolute top-full left-0 right-0 z-50 bg-zinc-900 border border-zinc-700 mt-1 max-h-[250px] overflow-y-auto">
          {filtered.length > 0 ? filtered.map(m => (
            <button key={m} onClick={() => { onChange(m); setQuery(''); setOpen(false); }} className={`w-full text-left px-3 py-2 text-sm hover:bg-zinc-800 transition-colors ${value === m ? 'text-[#C5A059]' : 'text-zinc-300'}`}>
              {m}
            </button>
          )) : (
            <p className="px-3 py-3 text-xs text-zinc-500">Nessun risultato</p>
          )}
        </div>
      )}
    </div>
  );
}

function estimatePrice(form) {
  const cat = CATEGORIES.find(c => c.slug === form.category);
  if (!cat || !form.year || !form.condition) return null;

  const basePriceRange = BASE_PRICES[form.category];
  const basePrice = (basePriceRange.min + basePriceRange.max) / 2;

  const age = Math.max(0, 2026 - parseInt(form.year));
  const ageFactor = Math.max(0.12, Math.pow(1 - cat.depRate, age));

  const hours = parseInt(form.hours) || 0;
  const hoursFactor = Math.max(0.25, 1 - (hours / cat.maxHrs) * 0.55);

  const cond = CONDITIONS.find(c => c.value === form.condition);
  const condFactor = cond?.factor || 0.7;

  const avionics = AVIONICS.find(a => a.value === form.avionics);
  const avionicsFactor = avionics?.factor || 1.0;

  const estimated = basePrice * ageFactor * hoursFactor * condFactor * avionicsFactor;
  const low = estimated * 0.85;
  const high = estimated * 1.15;

  const annualDepr = age > 0 ? ((basePrice * condFactor - estimated) / age) : 0;
  const costPerHour = hours > 0 ? ((basePrice * condFactor - estimated) / hours) : 0;

  return { low: Math.round(low), mid: Math.round(estimated), high: Math.round(high), newPrice: Math.round(basePrice * condFactor), annualDepr: Math.abs(Math.round(annualDepr)), costPerHour: Math.abs(Math.round(costPerHour)), age, hours, residualPct: Math.round((estimated / (basePrice * condFactor)) * 100) };
}

function formatEur(n) {
  return `€${n.toLocaleString('it-IT', { maximumFractionDigits: 0 })}`;
}

export default function PriceEstimator() {
  const [form, setForm] = useState({ category: '', manufacturer: '', model: '', variant: '', year: '', hours: '', condition: '', avionics: '' });
  const [result, setResult] = useState(null);

  const update = (k, v) => {
    setForm(p => {
      const next = { ...p, [k]: v };
      if (k === 'category') { next.manufacturer = ''; next.model = ''; }
      return next;
    });
    setResult(null);
  };

  const manufacturers = useMemo(() => CATEGORIES_MFR[form.category] || [], [form.category]);
  const canCalculate = form.category && form.manufacturer && form.year && form.condition;

  const calculate = () => setResult(estimatePrice(form));

  return (
    <div className="min-h-screen bg-[#09090B] pt-20" data-testid="price-estimator-page">
      <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <Link to="/services" className="inline-flex items-center gap-2 text-xs text-zinc-500 hover:text-[#C5A059] transition-colors mb-8">
          <ArrowLeft size={12} /> Tutti i servizi
        </Link>

        <div className="flex items-center gap-4 mb-2">
          <div className="w-14 h-14 bg-[#C5A059]/15 flex items-center justify-center">
            <DollarSign size={26} className="text-[#C5A059]" />
          </div>
          <div>
            <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-1">Strumento Gratuito</p>
            <h1 className="text-3xl md:text-4xl tracking-tight font-medium text-white flex items-center gap-3" style={{ fontFamily: "'Cabinet Grotesk', sans-serif" }}>
              Aircraft Price Estimator
              <span className="text-xs font-mono uppercase tracking-widest bg-[#C5A059]/20 text-[#C5A059] px-2.5 py-1 rounded-sm">Beta</span>
            </h1>
          </div>
        </div>

        {/* Beta Notice */}
        <div className="mt-4 mb-2 bg-amber-500/10 border border-amber-500/20 px-4 py-3" data-testid="beta-notice">
          <p className="text-xs text-amber-300/80 leading-relaxed">
            Questo strumento è in versione <strong>Beta</strong>. Le stime fornite sono indicative e basate su parametri generali di mercato. Per una valutazione professionale, contattaci tramite la sezione <a href="/services" className="text-[#C5A059] underline">Servizi</a>.
          </p>
        </div>

        <p className="text-sm text-zinc-400 mt-4 mb-10 max-w-2xl leading-relaxed">
          Ottieni una stima accurata del valore di mercato del tuo velivolo. Seleziona la categoria, cerca il costruttore, inserisci modello e dettagli per ricevere una valutazione basata su dati di mercato reali.
        </p>

        <div className="grid grid-cols-1 lg:grid-cols-5 gap-8">
          {/* Form */}
          <div className="lg:col-span-3 space-y-6">
            <div className="bg-zinc-900 border border-zinc-800 p-6">
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-5">1. Categoria & Costruttore</p>
              <div className="space-y-4">
                <div>
                  <Label className="text-zinc-400 text-xs mb-1.5 block">Categoria *</Label>
                  <Select value={form.category} onValueChange={v => update('category', v)}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-300 h-10 text-sm" data-testid="est-category"><SelectValue placeholder="Seleziona categoria" /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 z-[100]">
                      {CATEGORIES.map(c => <SelectItem key={c.slug} value={c.slug} className="text-zinc-300 text-sm">{c.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs mb-1.5 block">Costruttore *</Label>
                  {form.category ? (
                    <ManufacturerSearch value={form.manufacturer} onChange={v => update('manufacturer', v)} manufacturers={manufacturers} />
                  ) : (
                    <Input disabled placeholder="Seleziona prima la categoria" className="bg-zinc-800 border-zinc-700 text-zinc-500 h-10 text-sm" />
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label className="text-zinc-400 text-xs mb-1.5 block">Modello</Label>
                    <Input value={form.model} onChange={e => update('model', e.target.value)} placeholder="Es: SR22T, PA-28, R44..." className="bg-zinc-800 border-zinc-700 text-white h-10 text-sm" data-testid="est-model" />
                  </div>
                  <div>
                    <Label className="text-zinc-400 text-xs mb-1.5 block">Variante</Label>
                    <Input value={form.variant} onChange={e => update('variant', e.target.value)} placeholder="Es: G6, NG, II" className="bg-zinc-800 border-zinc-700 text-white h-10 text-sm" data-testid="est-variant" />
                  </div>
                </div>
              </div>
            </div>

            <div className="bg-zinc-900 border border-zinc-800 p-6">
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-5">2. Dettagli Tecnici</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-400 text-xs mb-1.5 block">Anno di Costruzione *</Label>
                  <Input type="number" value={form.year} onChange={e => update('year', e.target.value)} placeholder="Es: 2015" className="bg-zinc-800 border-zinc-700 text-white h-10 text-sm" data-testid="est-year" />
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs mb-1.5 block">Ore Totali (airframe)</Label>
                  <Input type="number" value={form.hours} onChange={e => update('hours', e.target.value)} placeholder="Es: 1200" className="bg-zinc-800 border-zinc-700 text-white h-10 text-sm font-mono" data-testid="est-hours" />
                </div>
              </div>
            </div>

            <div className="bg-zinc-900 border border-zinc-800 p-6">
              <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-5">3. Condizioni & Avionica</p>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label className="text-zinc-400 text-xs mb-1.5 block">Condizioni Generali *</Label>
                  <Select value={form.condition} onValueChange={v => update('condition', v)}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-300 h-10 text-sm" data-testid="est-condition"><SelectValue placeholder="Seleziona condizioni" /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 z-[100]">
                      {CONDITIONS.map(c => <SelectItem key={c.value} value={c.value} className="text-zinc-300 text-sm">{c.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-zinc-400 text-xs mb-1.5 block">Tipo Avionica</Label>
                  <Select value={form.avionics} onValueChange={v => update('avionics', v)}>
                    <SelectTrigger className="bg-zinc-800 border-zinc-700 text-zinc-300 h-10 text-sm" data-testid="est-avionics"><SelectValue placeholder="Seleziona avionica" /></SelectTrigger>
                    <SelectContent className="bg-zinc-900 border-zinc-800 z-[100]">
                      {AVIONICS.map(a => <SelectItem key={a.value} value={a.value} className="text-zinc-300 text-sm">{a.label}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </div>

            <button onClick={calculate} disabled={!canCalculate} className="w-full bg-[#C5A059] text-black py-3.5 text-sm font-medium hover:bg-[#D4B271] transition-colors disabled:opacity-40 disabled:cursor-not-allowed flex items-center justify-center gap-2" data-testid="estimate-btn">
              <DollarSign size={16} /> Calcola Stima di Mercato
            </button>
          </div>

          {/* Result */}
          <div className="lg:col-span-2 space-y-4">
            {result ? (
              <>
                <div className="bg-zinc-900 border border-[#C5A059]/30 p-6" data-testid="estimate-result">
                  <p className="text-xs font-mono uppercase tracking-[0.2em] text-[#C5A059] mb-1">Valore Stimato</p>
                  <p className="text-[10px] text-zinc-600 mb-4">
                    {form.manufacturer} {form.model} {form.variant && `(${form.variant})`} &middot; {form.year} &middot; {parseInt(form.hours) || 0}h
                  </p>
                  <div className="text-center py-4 border-y border-zinc-800">
                    <p className="text-4xl font-bold text-white font-mono" data-testid="estimate-mid">{formatEur(result.mid)}</p>
                    <p className="text-xs text-zinc-500 mt-1">Valore medio di mercato</p>
                  </div>
                  <div className="flex justify-between mt-4">
                    <div className="text-center">
                      <p className="text-[10px] text-zinc-600 uppercase tracking-wider mb-0.5">Vendita rapida</p>
                      <p className="text-sm text-zinc-300 font-mono">{formatEur(result.low)}</p>
                    </div>
                    <div className="text-center">
                      <p className="text-[10px] text-zinc-600 uppercase tracking-wider mb-0.5">Prezzo retail</p>
                      <p className="text-sm text-zinc-300 font-mono">{formatEur(result.high)}</p>
                    </div>
                  </div>
                </div>

                <div className="bg-zinc-900 border border-zinc-800 p-5 space-y-4">
                  <p className="text-xs font-mono uppercase tracking-[0.2em] text-zinc-500 mb-1">Analisi Deprezzamento</p>
                  <div className="space-y-3">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2"><TrendingDown size={13} className="text-red-400" /><span className="text-xs text-zinc-400">Depr. totale stimato</span></div>
                      <span className="text-xs text-red-400 font-mono">-{formatEur(result.newPrice - result.mid)}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2"><Clock size={13} className="text-blue-400" /><span className="text-xs text-zinc-400">Depr. annuo medio</span></div>
                      <span className="text-xs text-zinc-300 font-mono">{result.age > 0 ? formatEur(result.annualDepr) + '/anno' : '—'}</span>
                    </div>
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-2"><Wrench size={13} className="text-amber-400" /><span className="text-xs text-zinc-400">Costo depr./ora</span></div>
                      <span className="text-xs text-zinc-300 font-mono">{result.hours > 0 ? formatEur(result.costPerHour) + '/h' : '—'}</span>
                    </div>
                  </div>
                  <div className="pt-3 border-t border-zinc-800">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[10px] text-zinc-500">Valore residuo</span>
                      <span className="text-[10px] text-[#C5A059] font-mono">{result.residualPct}%</span>
                    </div>
                    <div className="h-2 bg-zinc-800 overflow-hidden">
                      <div className="h-full bg-gradient-to-r from-[#C5A059] to-[#D4B271]" style={{ width: `${result.residualPct}%` }} />
                    </div>
                  </div>
                </div>

                <div className="flex gap-3">
                  <Link to="/create-listing" className="flex-1 flex items-center justify-center gap-2 bg-[#C5A059] text-black py-2.5 text-xs font-medium hover:bg-[#D4B271] transition-colors">
                    Vendi Ora <ChevronRight size={12} />
                  </Link>
                  <Link to="/listings" className="flex-1 flex items-center justify-center gap-2 border border-zinc-700 text-zinc-300 py-2.5 text-xs hover:border-zinc-500 transition-colors">
                    Cerca Annunci
                  </Link>
                </div>

                <div className="p-3 bg-zinc-800/30 border border-zinc-700/30">
                  <p className="text-[10px] text-zinc-600 leading-relaxed">Stima basata su dati di mercato aggregati. Il valore reale varia in base a manutenzione, modifiche STC, stato motore e mercato locale.</p>
                </div>
              </>
            ) : (
              <>
                <div className="bg-zinc-900 border border-zinc-800 p-8 text-center">
                  <BarChart3 size={36} className="mx-auto mb-3 text-zinc-700" />
                  <p className="text-sm text-zinc-400 mb-1">Inserisci i dati del velivolo</p>
                  <p className="text-xs text-zinc-600">Seleziona categoria, costruttore, anno e condizioni</p>
                </div>
                <div className="bg-zinc-900 border border-zinc-800 p-5 space-y-3">
                  <p className="text-xs font-medium text-white mb-2">Come calcoliamo la stima</p>
                  <div className="flex items-start gap-3"><BarChart3 size={14} className="text-[#C5A059] mt-0.5 shrink-0" /><p className="text-[11px] text-zinc-400">Fascia di prezzo per categoria e costruttore</p></div>
                  <div className="flex items-start gap-3"><Clock size={14} className="text-[#C5A059] mt-0.5 shrink-0" /><p className="text-[11px] text-zinc-400">Curva ammortamento su eta e ore di volo</p></div>
                  <div className="flex items-start gap-3"><Wrench size={14} className="text-[#C5A059] mt-0.5 shrink-0" /><p className="text-[11px] text-zinc-400">6 livelli condizioni: da nuovo a da restaurare</p></div>
                  <div className="flex items-start gap-3"><Award size={14} className="text-[#C5A059] mt-0.5 shrink-0" /><p className="text-[11px] text-zinc-400">5 tipi avionica: da analogica a glass avanzato</p></div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
